import {
    parseDaypartTag,
    QUERY_DAYPART_TAG,
    readQueryString,
    readSelectedFishGroupId,
} from '@/contexts/cageFilter/params';
import type { UseGetCageOverviewParams } from '@/domain/cages/overview.types';
import type { CageDashboardPrefetchTarget } from '@/domain/cages/prefetchCageDashboard';
import { resolveWeightDaypartTag } from '@/routes/Client/Cage/utils/filter-graph-dayparts';

import { penStepHref, penStepNeighbors } from './penStep';
import type { BreadcrumbCage } from './types';

const PEN_ID = /\/pen\/(\d+)(?:\/|$)/;

function searchParamsOf(search: string): URLSearchParams {
    const query = search.startsWith('?') ? search.slice(1) : search;
    return new URLSearchParams(query);
}

/**
 * The overview params the cage page will use for this search: the carried
 * `date-from` / `date-to`, the daypart (`all` on a smolt pen), and the cohort
 * when the caller already resolved one.
 */
export function cageOverviewParamsFromSearch(
    search: string,
    smolt: boolean,
    fishGroupId?: number
): UseGetCageOverviewParams {
    const params = searchParamsOf(search);
    return {
        from: readQueryString(params.get('date-from')),
        to: readQueryString(params.get('date-to')),
        daypartTag: resolveWeightDaypartTag(smolt, parseDaypartTag(params.get(QUERY_DAYPART_TAG))),
        ...(fishGroupId != null ? { fishGroupId } : {}),
    };
}

/**
 * The dashboard prefetch for a pen-step href. The href already carries the date
 * range, the daypart param and the destination fish group; smolt still forces
 * `all`, which is what the destination page requests once its cage type is known.
 */
export function cageDashboardTargetFromPenHref(
    href: string,
    smolt: boolean
): CageDashboardPrefetchTarget | null {
    const queryAt = href.indexOf('?');
    const path = queryAt === -1 ? href : href.slice(0, queryAt);
    const match = PEN_ID.exec(path);
    if (match == null) {
        return null;
    }

    const cageId = Number(match[1]);
    if (!Number.isInteger(cageId) || cageId <= 0) {
        return null;
    }

    const params = cageOverviewParamsFromSearch(queryAt === -1 ? '' : href.slice(queryAt), smolt);
    const fishGroupId = readSelectedFishGroupId(queryAt === -1 ? '' : href.slice(queryAt));
    return {
        cageId,
        from: params.from,
        to: params.to,
        daypartTag: params.daypartTag ?? 'day',
        ...(fishGroupId != null ? { fishGroupId } : {}),
    };
}

/**
 * Previous and next only, each with the href the stepper would open.
 * The menu drops the date range; this follows the stepper, not the menu.
 */
export function penNeighborDashboardTargets(input: {
    cages: readonly BreadcrumbCage[];
    currentCageId: number | undefined;
    clientId: number;
    locationId: number;
    pagePath: string;
    search: string;
    tracksFishGroups: boolean;
}): CageDashboardPrefetchTarget[] {
    const neighbors = penStepNeighbors(input.cages, input.currentCageId);
    const targets: CageDashboardPrefetchTarget[] = [];

    for (const cage of [neighbors.previous, neighbors.next]) {
        if (cage == null) {
            continue;
        }
        const href = penStepHref({
            clientId: input.clientId,
            locationId: input.locationId,
            cage,
            pagePath: input.pagePath,
            search: input.search,
            tracksFishGroups: input.tracksFishGroups,
        });
        const target = cageDashboardTargetFromPenHref(href, cage.smolt === true);
        if (target != null && target.cageId !== input.currentCageId) {
            targets.push(target);
        }
    }

    return targets;
}
